import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import organisationService from "./organisationService";

/**
 * State for the public organisation ranking pages.
 *
 * Deliberately does not dispatch alert actions the way the other slices do:
 * this runs in the public tree, which has no AlertProvider and no auth. Errors
 * are held here and rendered by the page itself.
 *
 * The admin CRUD screen talks to organisationService directly with local
 * component state - that data is not shared with anything else.
 */

const initialState = {
  organisation: null,
  rankings: [],
  window: null,
  isLoading: false,
  isRankingsLoading: false,
  isError: false,
  notFound: false,
  message: "",
  // The gender the most recently dispatched fetch asked for. Responses for
  // older requests are ignored so a slow response can't overwrite the
  // current tab's data (tab-switch race).
  requestedGender: null,
};

export const fetchPublicOrganisation = createAsyncThunk(
  "organisation/fetchPublic",
  async (slug, thunkAPI) => {
    try {
      const response = await organisationService.getPublicOrganisation(slug);
      return response.data.organisation;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status ?? null,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to load this ranking page",
      });
    }
  },
);

export const fetchPublicRankings = createAsyncThunk(
  "organisation/fetchPublicRankings",
  async (params, thunkAPI) => {
    try {
      const response = await organisationService.getPublicRankings(params);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status ?? null,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to load rankings",
      });
    }
  },
);

const organisationSlice = createSlice({
  name: "organisation",
  initialState,
  reducers: {
    reset: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicOrganisation.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.notFound = false;
      })
      .addCase(fetchPublicOrganisation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organisation = action.payload;
      })
      .addCase(fetchPublicOrganisation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.notFound = action.payload?.status === 404;
        state.message = action.payload?.message || "Error loading page";
      })
      .addCase(fetchPublicRankings.pending, (state, action) => {
        state.isRankingsLoading = true;
        state.requestedGender = action.meta.arg?.gender ?? null;
      })
      .addCase(fetchPublicRankings.fulfilled, (state, action) => {
        // Ignore responses for older requests: a slow "Men" response must
        // not overwrite the data of the "Women" tab the user switched to.
        if ((action.meta.arg?.gender ?? null) !== state.requestedGender) {
          return;
        }
        state.isRankingsLoading = false;
        state.rankings = action.payload.rankings || [];
        state.window = action.payload.window || null;
        // The rankings response carries the organisation too, so a direct hit
        // on this endpoint does not need a second request.
        if (action.payload.organisation) {
          state.organisation = action.payload.organisation;
        }
      })
      .addCase(fetchPublicRankings.rejected, (state, action) => {
        // Same guard: a stale failure must not clobber the current tab.
        if ((action.meta.arg?.gender ?? null) !== state.requestedGender) {
          return;
        }
        state.isRankingsLoading = false;
        state.isError = true;
        state.notFound = action.payload?.status === 404;
        state.message = action.payload?.message || "Error loading rankings";
      });
  },
});

export const { reset } = organisationSlice.actions;
export default organisationSlice.reducer;
